import React, { Component } from "react";
import { connect } from "react-redux";
import styled from "styled-components";

import { CPage, CButton } from "@appcues/component-library";
import { getFlattenedClausesFromNestedConditions } from "@appcues/libcues";

import IndexPage from "components/Common/IndexPage";
import NpsQueryResults from "components/Common/QueryResults/NpsQueryResults";
import Dashboard from "components/SatisfactionSurveys/SatisfactionSurveysIndex/Dashboard";
import { selectFlowStatus } from "reducers/account/flowStatus";

import { isCustomer } from "helpers/plans";
import { getRolling30DayTimePeriod } from "helpers/satisfaction";

import { STEP_TYPE_IDS } from "constants/stepTypes";
import { baseRule } from "constants/account/rules";
import { SATISFACTION_BASE_CONDITIONS } from "constants/satisfaction/data";
import {
  CREATE_SATISFACTION_SURVEY_MODAL,
  NPS_TRIAL_EXPIRATION_MODAL
} from "constants/globals";
import { millisecondsPerDay } from "constants/account/conditions";

import { selectUser, selectUserId } from "reducers/user";
import {
  selectAccountMeta,
  selectAccountMetaSynced
} from "reducers/account/meta";
import {
  selectAccountRule,
  selectAccountRules,
  selectAccountRulesSynced
} from "reducers/account/rules";
import { selectUserRole } from "reducers/account/users";

import { selectUiCurrentModalKey } from "reducers/currentModal";
import * as satisfactionSelectors from "selectors/satisfaction";

import { trackEvent } from "actions/events";

import * as satisfactionActions from "actions/satisfaction";
import { updateAccountMeta } from "actions/account/meta";
import { showModal, hideModal } from "actions/currentModal";
import { navigate } from "actions/routing";

import { selectAccountSatisfactionSynced } from "reducers/account/satisfaction";
import { selectIsInstalled } from "reducers/account/installedDomains";

import { callApi as fetchRules } from "actions/account/rules";
import { fetchDashboardStats } from "actions/dashboard";

import NpsEmptyState from "./NpsEmptyState";

export class SatisfactionSurveysIndex extends Component {
  state = {
    clickedCreateSurvey: false,
    showNPSEmptyState: false,
    conditions: null,
    satisfactionRule: null
  };

  componentWillMount() {
    this.componentWillReceiveProps(this.props);
  }

  componentWillReceiveProps(nextProps) {
    const {
      meta,
      firebaseSynced,
      satisfactionId,
      satisfaction,
      satisfactionSynced
    } = nextProps;

    const { meta: prevMeta } = this.props;
    const { onSave, currentModal } = this.props;

    const satisfactionSyncedWithNoSurvey =
      !satisfactionId && satisfactionSynced && firebaseSynced;
    const isCreateModalOpen = currentModal === CREATE_SATISFACTION_SURVEY_MODAL;
    const isExpirationModalOpen = currentModal === NPS_TRIAL_EXPIRATION_MODAL;

    if (satisfactionSyncedWithNoSurvey && !isCreateModalOpen) {
      this.setShowNPSEmptyState();
    } else if (firebaseSynced && satisfaction) {
      if (
        firebaseSynced &&
        meta.isNPSProductTrialExpired &&
        !isExpirationModalOpen
      ) {
        this.showNPSTrialExpirationModal();
      }

      // Non-Customers will have an NPS Trial period of 30 days
      // based on the date their survey was created.
      if (
        !isCustomer(meta) &&
        !meta.isNPSProductTrialExpired &&
        !prevMeta.isNPSProductTrialExpired
      ) {
        const now = new Date().getTime();
        const trialLength = parseInt(
          (now - satisfaction.createdAt) / millisecondsPerDay
        );
        if (trialLength > meta.npsTrialLengthDays) {
          onSave({
            isNPSProductTrialExpired: true
          });
        }
      }
    }
  }

  componentWillUpdate(nextProps, nextState) {
    const { clickedCreateSurvey } = nextState;
    const { satisfactionId: nextSatisfactionId } = nextProps;
    const { satisfactionId, hideModal, navigate } = this.props;

    const hasCreatedNPS = !satisfactionId && nextSatisfactionId;
    if (clickedCreateSurvey && hasCreatedNPS) {
      hideModal();
      navigate(`/nps/${nextSatisfactionId}/edit`);
    }
  }

  componentWillUnmount() {
    this.props.hideModal();
  }

  handleClickEdit = () => {
    const { satisfactionId, navigate } = this.props;
    if (satisfactionId) {
      navigate(`/nps/${satisfactionId}/edit`);
    } else {
      this.setShowNPSEmptyState();
    }
  };

  setShowNPSEmptyState() {
    const satisfactionRule = {
      ...baseRule,
      frequency: "every_time",
      contentType: STEP_TYPE_IDS.SATISFACTION,
      widgetFilter: "exclude"
    };
    const conditions = getFlattenedClausesFromNestedConditions(
      SATISFACTION_BASE_CONDITIONS
    );
    this.setState({ showNPSEmptyState: true, conditions, satisfactionRule });
  }

  showNPSTrialExpirationModal() {
    const { satisfactionId, showModal, navigate } = this.props;
    showModal(NPS_TRIAL_EXPIRATION_MODAL, {
      stepId: satisfactionId,
      onClickSubscribeCallback: () => navigate("/subscription")
    });
  }

  render() {
    const { satisfactionId, meta, firebaseSynced, currentModal } = this.props;

    const rolling30DayTimePeriod = getRolling30DayTimePeriod();

    const surveyControls = firebaseSynced ? (
      <CButton type="primary" onClick={this.handleClickEdit}>
        Edit Survey
      </CButton>
    ) : null;

    return (
      <NpsQueryResults npsId={satisfactionId}>
        {results => {
          const {
            npsScore,
            responseCount,
            promoters,
            detractors,
            neutrals,
            qualitativeCount,
            shownCount
          } = results || {};
          const wholeNpsScore = Math.floor(npsScore) || 0;
          const { showNPSEmptyState } = this.state;

          return (
            <IndexPage
              title="Your NPS Survey"
              description={`Time Period: Rolling 30 Days from ${rolling30DayTimePeriod.thirtyDaysAgo} - ${rolling30DayTimePeriod.today}`}
              subHeaderActions={
                !this.state.showNPSEmptyState ? surveyControls : false
              }
            >
              <CPage.Container>
                {!showNPSEmptyState && results && (
                  <Dashboard
                    firebaseSynced={firebaseSynced}
                    isNPSProductTrialExpired={meta.isNPSProductTrialExpired}
                    currentModal={currentModal}
                    resultsSynced={!!results}
                    promoters={promoters}
                    neutrals={neutrals}
                    detractors={detractors}
                    shownCount={shownCount}
                    qualitativeCount={qualitativeCount}
                    responseCount={responseCount}
                    npsScore={wholeNpsScore}
                  />
                )}
                {showNPSEmptyState && (
                  <NpsEmptyState
                    trackEvent={trackEvent}
                    stepId="new"
                    meta={this.props.meta}
                    rule={this.state.satisfactionRule}
                    conditions={this.state.conditions}
                    createSatisfactionWithRule={
                      this.props.createSatisfactionWithRule
                    }
                    onCreateCallback={() =>
                      this.setState({ clickedCreateSurvey: true })
                    }
                  />
                )}
              </CPage.Container>
            </IndexPage>
          );
        }}
      </NpsQueryResults>
    );
  }
}

function mapStateToProps(state) {
  const meta = selectAccountMeta(state);
  const accountId = meta.id;
  const accountUuid = meta.uuid;

  const satisfactionState = satisfactionSelectors.selectSatisfactionState(
    state
  );

  const satisfactionSynced = selectAccountSatisfactionSynced(state);

  const satisfactionId = satisfactionState.satisfaction
    ? satisfactionSelectors.selectAccountFirstSatisfactionId(state)
    : null;
  const satisfactionSurvey =
    satisfactionState.satisfaction && satisfactionId
      ? satisfactionSelectors.selectAccountSatisfactionSurvey(
          state,
          satisfactionId
        )
      : null;

  return {
    satisfactionId,
    satisfaction: satisfactionSurvey,
    satisfactionSynced,
    exportFormat: satisfactionState.satisfactionExportFormat,
    role: selectUserRole(state, selectUserId(state)),
    meta,
    isInstalled: selectIsInstalled(state),
    accountId,
    accountUuid,
    rules: selectAccountRules(state),
    rule: selectAccountRule(state, satisfactionId),
    contentStatus: selectFlowStatus(state, satisfactionId),
    user: selectUser(state),
    currentModal: selectUiCurrentModalKey(state),
    firebaseSynced:
      satisfactionSynced &&
      selectAccountRulesSynced(state) &&
      selectAccountMetaSynced(state)
  };
}

const mapDispatchToProps = {
  fetchDashboardStats,
  fetchRules,
  toggleFormat: satisfactionActions.toggleSatisfactionExportFormat,
  createSatisfactionWithRule: satisfactionActions.createSatisfactionWithRule,
  onSave: updateAccountMeta,
  showModal,
  hideModal,
  navigate
};

const mergeProps = (
  stateProps,
  { fetchDashboardStats, fetchRules, ...dispatchProps },
  ownProps
) => ({
  ...ownProps,
  ...stateProps,
  ...dispatchProps,
  onLoad: () => fetchDashboardStats() && fetchRules()
});

export default styled(
  connect(
    mapStateToProps,
    mapDispatchToProps,
    mergeProps
  )(SatisfactionSurveysIndex)
)`
  header {
    align-items: baseline;
  }

  .title h2 {
    margin: 5px 0;
  }

  .header-actions {
    width: 35%;

    .header-actions-survey {
      display: flex;
      width: 100%;
      align-items: center;
      justify-content: flex-end;

      .publish-bar {
        flex-grow: inherit;
        .publish-buttons-container {
          padding: 0px 8px;

          > .button.button-primary {
            display: none;
          }
        }
      }
    }
  }
`;
